import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Dialog,
  DialogContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { MarketButton, MarketLink } from '@market/react';
import CloseIcon from '@square-icons/react/24/UI/x';
import QuantitySelector from 'components/common/quantity_selector';
import LazyImage from 'components/common/lazy_image';
import RichTextContent from 'components/common/contentful/rich_text_content';
import { updateItem } from 'utilities/cart';
import { localizedShopPath } from 'utilities/locales';
import { updateCartCount } from 'utilities/nav';
import { shopImageSrcSet } from 'utilities/images';
import { trackAddToCart } from 'components/pages/product/helpers';
import { trackCdp } from '../../../utilities/tracking';

const TrackingEventTypes = {
  PdpLinkClicked: 'PdpLinkClicked',
};

const AccessoryModal = (props) => {
  const {
    open,
    name,
    displayPrice,
    description,
    imgSrc,
    variantToken,
    slug,
    trackingData,
    cart,
    handleClose,
    cardQuantities,
    setCardQuantities,
    setToast,
    setSubtotal,
  } = props;

  const theme = useTheme();
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [requesting, setRequesting] = useState(false);

  const onClose = () => {
    handleClose();
    setQuantity(1);
  };

  const updateCardQuantities = (slug, updatedQuantity) => {
    setCardQuantities({ ...cardQuantities, [slug]: updatedQuantity });
  };

  const onAddToCartClick = () => {
    setRequesting(true);

    const onSuccess = (data) => {
      setRequesting(false);

      setToast({
        timeout: 3000,
        open: true,
        message: t('productPage.addedToCart', { name }),
        variant: 'success',
        action: (
          <MarketLink href={`${localizedShopPath()}/cart`}>
            {t('productPage.viewCart')}
          </MarketLink>
        ),
      });
      updateCartCount(data.item_count);
      updateCardQuantities(slug, cardQuantities[slug] + quantity);
      setSubtotal(data.subtotal);
      trackAddToCart({
        mainProductTrackingData: trackingData,
        cartNumber: cart.number,
        quantity,
      });

      onClose();
    };

    const onError = () => {
      setRequesting(false);
      setToast({
        open: true,
        message: t('productPage.addToCartError', { name }),
        variant: 'critical',
      });
    };

    updateItem(variantToken, quantity, [], onSuccess, onError);
  };

  const trackEvent = ({ eventName, params }) => {
    const defaultParams = {
      feature: 'shop',
      cartNumber: cart.number,
    };

    trackCdp({
      name: `AccessoryModal:${eventName}`,
      params: { ...defaultParams, ...params },
    });
  };

  const onPdpLinkClick = () => {
    trackEvent({
      eventName: TrackingEventTypes.PdpLinkClicked,
      params: {
        product: { ...trackingData },
      },
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      fullScreen={useMediaQuery(theme.breakpoints.down('sm'))}
      open={open}
      onClose={onClose}
      transitionDuration={300}
      disableScrollLock
    >
      <DialogContent sx={{ pt: 2, px: 4, pb: 4 }}>
        <CloseIcon
          onClick={onClose}
          style={{ float: 'right', cursor: 'pointer' }}
        />
        <Grid container>
          <Grid
            item
            xs={12}
            sm={5}
            sx={{ textAlign: 'center', pb: 3, pr: { xs: 0, sm: 2.5 } }}
          >
            <LazyImage
              alt={name}
              srcSet={shopImageSrcSet({
                baseUrl: imgSrc,
                type: 'productModal',
              })}
              aspectRatio={1}
            />
            <MarketLink
              href={`${localizedShopPath()}/products/${slug}`}
              target="_blank"
              onClick={onPdpLinkClick}
            >
              {t('productPage.goToProductPage')}
            </MarketLink>
          </Grid>
          <Grid item xs={12} sm={7}>
            <Box component="h2" sx={{ my: 2 }}>
              {name}
            </Box>
            <p>{displayPrice}</p>
            <RichTextContent content={description} my={2} />
            <Box display="flex">
              <QuantitySelector
                value={quantity}
                minimumValue={1}
                maximumValue={9}
                onValueChange={(newValue) => setQuantity(newValue)}
                ariaQuantityName={name}
              />
              <Box flexGrow={1} ml={2}>
                <MarketButton
                  {...(requesting && { disabled: true })}
                  id="add-to-cart-btn"
                  rank="primary"
                  onClick={onAddToCartClick}
                  style={{ width: '100%' }}
                >
                  {t('productPage.addToCart')}
                </MarketButton>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default AccessoryModal;
